import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useRef, useState } from "react";
import WorldMap from "@/components/WorldMap";
import QuoteDialog from "@/components/QuoteDialog";
import { easeExpo } from "@/lib/motion";

const HEADLINE_LINES = [
  "Connecting Global",
  "Electronics Supply",
  <span key="sc">
    Chains <span className="text-electric-500">.</span>
  </span>,
];

const PRODUCTS = [
  "CPUs",
  "SSDs",
  "Memory",
  "ICs",
  "Motherboards",
  "Networking",
  "Enterprise Storage",
];

export default function Hero() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const mapY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const mapScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const headlineY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -20]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={heroRef}
      className="relative min-h-[100svh] pt-24 md:pt-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Ambient gradient blobs (parallax slower) */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <div
          className="aurora animate-aurora"
          style={{
            width: 620,
            height: 620,
            top: -140,
            left: -180,
            background:
              "radial-gradient(circle, rgba(0,229,255,0.28) 0%, rgba(0,229,255,0) 60%)",
          }}
        />
        <div
          className="aurora animate-aurora"
          style={{
            width: 720,
            height: 720,
            bottom: -240,
            right: -220,
            background:
              "radial-gradient(circle, rgba(20,86,199,0.35) 0%, rgba(20,86,199,0) 60%)",
            animationDelay: "-6s",
          }}
        />
      </motion.div>

      {/* Faint grid */}
      <div className="absolute inset-0 bg-grid mask-radial opacity-70" />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-start pt-6">
        {/* LEFT: kinetic headline */}
        <motion.div style={{ y: headlineY }} className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: easeExpo, delay: 0.05 }}
            className="flex items-center gap-3 text-[11px] font-mono tracking-[0.22em] uppercase text-slate-400"
          >
            <span className="w-8 h-px bg-electric-500/70" />
            <span>Est. 2026 · Hong Kong · Singapore</span>
          </motion.div>

          <h1
            className="mt-8 font-display font-extrabold text-white leading-[0.94] tracking-[-0.03em] text-[13vw] sm:text-6xl md:text-7xl lg:text-[86px] xl:text-[100px]"
            data-testid="hero-headline"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="reveal-mask block">
                <motion.span
                  className="block will-change-transform"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 1.05,
                    ease: easeExpo,
                    delay: 0.15 + i * 0.11,
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: easeExpo, delay: 0.7 }}
            className="mt-10 max-w-[560px] text-slate-300 text-lg leading-relaxed text-pretty"
          >
            OmniCoreTek is the global exchange for hard-to-find semiconductors and
            enterprise components — trusted by OEMs, EMS partners and hyperscalers
            across 15+ countries.
          </motion.p>

          <motion.ul
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.045, delayChildren: 0.85 } },
            }}
            className="mt-6 flex flex-wrap gap-x-2 gap-y-2"
            data-testid="hero-product-chips"
          >
            {PRODUCTS.map((p) => (
              <motion.li
                key={p}
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  show: { opacity: 1, y: 0 },
                }}
                className="text-[11px] font-mono tracking-[0.14em] uppercase text-slate-300 px-2.5 py-1 border border-white/10 rounded-full"
              >
                {p}
              </motion.li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: easeExpo, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-request-quote-button"
              onClick={() => setQuoteOpen(true)}
              className="btn-electric rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Request Quote <ArrowUpRight className="w-4 h-4" />
            </button>
            <a
              href="#products"
              data-testid="hero-browse-products-button"
              className="btn-ghost rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Browse Products
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.25 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-[520px]"
          >
            {[
              ["15+", "Countries served"],
              ["24h", "Quote turnaround"],
              ["12K+", "SKUs in orbit"],
            ].map(([k, v]) => (
              <div key={v}>
                <div className="font-display text-3xl md:text-4xl text-white tracking-tight">
                  {k}
                </div>
                <div className="mt-1 text-[11px] font-mono tracking-[0.14em] uppercase text-slate-500">
                  {v}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT: World Map with float in + scroll parallax */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: easeExpo, delay: 0.4 }}
          className="lg:col-span-6 relative"
        >
          <motion.div style={{ y: mapY, scale: mapScale }} className="relative">
            {/* Aperture ring */}
            <div className="absolute -inset-8 rounded-[32px] border border-white/5 pointer-events-none" />
            <div className="absolute -inset-2 rounded-[26px] pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 60% 30%, rgba(0,229,255,0.14), transparent 60%)",
              }}
            />
            <div className="relative glass rounded-2xl p-3 md:p-4">
              <div className="flex items-center justify-between px-3 pt-1 pb-3">
                <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-slate-400">
                  / Live Trade Corridors
                </div>
                <div className="flex items-center gap-2 text-[10px] font-mono text-electric-500">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-electric-500" />
                  </span>
                  LIVE
                </div>
              </div>
              <WorldMap />
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 px-2 pt-3 pb-1 text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">
                {["USA", "Europe", "UAE", "India", "Singapore", "Hong Kong", "China"].map(
                  (l) => (
                    <div key={l} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-electric-500" />
                      {l}
                    </div>
                  )
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.a
        href="#network"
        aria-label="Scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-slate-500 hover:text-white"
      >
        <span className="text-[10px] font-mono tracking-[0.22em] uppercase">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.a>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </section>
  );
}
